import React, { useState } from 'react';
import styled from 'styled-components/macro';
import Cookies from 'universal-cookie';
import { IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { TextString } from '../components/Text';
import useTranslations from '../hooks/useTranslations';
import getTenant from '../utils/getTenant';
import Colors from './Colors';
import DontPrint from './DontPrint';

const cookies = new Cookies();

const COOKIE_NAME_PREFIX = 'omnitrack-cookie-notice-dismissed';
const ONE_YEAR_IN_SECONDS = 365 * 24 * 60 * 60;

// getTenant() reads window.location.pathname live, so build the name fresh on
// every call rather than caching it once at module scope (unlike `cookies` above).
export const getCookieName = () => `${COOKIE_NAME_PREFIX}-${getTenant()}`;

/** Above app chrome (NavBar, LocaleWidget); below MUI dialogs (~1300) and the global loader. */
const COOKIE_NOTICE_Z_INDEX = 1250;

const Bar = styled(DontPrint)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${COOKIE_NOTICE_Z_INDEX};
  box-sizing: border-box;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid ${Colors.OT_GREY};
  color: ${({ theme }) => theme.palette.text.primary};
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;

  /* clearfix: CloseButton is floated, so Bar's own auto height needs to contain it */
  &:after {
    content: '';
    display: table;
    clear: both;
  }

  a {
    color: ${({ theme }) => theme.palette.turquoise};
  }
`;

const CloseButton = styled(IconButton)`
  && {
    float: right;
    margin: 0 0 8px 12px;
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 0;
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: #fff;
  }

  &&:hover {
    background-color: ${({ theme }) => theme.palette.primary.dark};
  }

  svg {
    font-size: 18px;
  }
`;

const CookieNoticeBar = ({ onDismiss }) => {
  const t = useTranslations();

  const dismiss = () => {
    cookies.set(getCookieName(), '1', {
      path: '/',
      maxAge: ONE_YEAR_IN_SECONDS
    });
    onDismiss();
  };

  return (
    <Bar
      data-testid="cookie-notice"
      role="region"
      aria-label={t('cookieNotice.title')}
    >
      <CloseButton
        onClick={dismiss}
        aria-label={t('cookieNotice.dismiss')}
        data-testid="cookie-notice-close"
        size="small"
      >
        <CloseIcon />
      </CloseButton>
      <TextString content="cookieNotice.body" />
    </Bar>
  );
};

const CookieNotice = () => {
  const [isDismissed, setIsDismissed] = useState(() =>
    Boolean(cookies.get(getCookieName()))
  );

  if (isDismissed) {
    return null;
  }

  return <CookieNoticeBar onDismiss={() => setIsDismissed(true)} />;
};

export default CookieNotice;
